import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { medicalRecordService } from '../services/medicalRecordService.js';
import './ConsultationForm.css';

const EXAM_OPTIONS = [
  { id: 'hemograma', label: 'Hemograma' },
  { id: 'bioquimico', label: 'Bioquímico' },
  { id: 'raio_x', label: 'Raio-X' },
  { id: 'ultrassom', label: 'Ultrassom' },
  { id: 'urinanalise', label: 'Urinálise' },
  { id: 'parasitologico', label: 'Parasitológico' },
];

const GENERAL_CONDITION_OPTIONS = [
  { value: 'good', label: 'Bom' },
  { value: 'fair', label: 'Regular' },
  { value: 'poor', label: 'Ruim' },
  { value: 'critical', label: 'Crítico' },
];

const HYDRATION_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'mild', label: 'Leve (< 5%)' },
  { value: 'moderate', label: 'Moderada (5-10%)' },
  { value: 'severe', label: 'Grave (> 10%)' },
];

const NEEDS_RETURN_OPTIONS = [
  { value: 'yes', label: 'Sim' },
  { value: 'no', label: 'Não' },
  { value: 'if_needed', label: 'Se necessário' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Baixa' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

const FREQUENCY_TIMES_OPTIONS = [
  { value: 'once', label: '1 vez' },
  { value: 'twice', label: '2 vezes' },
  { value: 'three_times', label: '3 vezes' },
  { value: 'four_times', label: '4 vezes' },
];

const FREQUENCY_TYPE_OPTIONS = [
  { value: 'by_day', label: 'ao dia' },
  { value: 'by_month', label: 'ao mês' },
  { value: 'by_year', label: 'ao ano' },
];

const DURATION_TYPE_OPTIONS = [
  { value: 'days', label: 'dias' },
  { value: 'weeks', label: 'semanas' },
  { value: 'months', label: 'meses' },
];

const EMPTY_FORM = {
  chiefComplaint: '',
  historyAndObservations: '',
  weight: '',
  temperature: '',
  heartRate: '',
  respiratoryRate: '',
  generalCondition: '',
  hydrationLevel: '',
  physicalExamNotes: '',
  clinicalDiagnosis: '',
  requestedExams: [],
  generalGuidelines: '',
  needsReturn: '',
  returnDeadline: '',
  returnPriority: '',
  returnNotes: '',
  internalNotes: '',
};

const EMPTY_MED = {
  name: '', dosage: '',
  frequencyTimes: '', frequencyType: '',
  durationTimes: '', durationType: '',
  startDate: '', endDate: '',
  notes: '',
};

function Section({ title, icon, children, defaultOpen = false, filled = false, count = null }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="cf-section">
      <div className={`cf-section-header ${open ? 'open' : ''}`} onClick={() => setOpen(!open)}>
        <span className="cf-section-title">
          <span className="cf-section-icon">{icon}</span>
          {title}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {filled && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              fontSize: '11px', fontWeight: '700',
              color: '#166534', background: '#DCFCE7',
              padding: '2px 8px', borderRadius: '20px',
            }}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="#166534" stroke="none"><circle cx="12" cy="12" r="12" /></svg>
              {count !== null ? count : 'Preenchido'}
            </span>
          )}
          <svg
            width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', color: '#999' }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>
      {open && <div className="cf-section-body">{children}</div>}
    </div>
  );
}

function ConsultationForm({ petId, entityId, consultationId: initialConsultationId, scheduleRef, onSaved, onCancel }) {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [medications, setMedications] = useState([]);
  const [medForm, setMedForm] = useState({ ...EMPTY_MED });
  const [editingMedIndex, setEditingMedIndex] = useState(null);
  const [showMedForm, setShowMedForm] = useState(false);
  const [savingMed, setSavingMed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [consultationId, setConsultationId] = useState(initialConsultationId || null);
  const [linkedScheduleId, setLinkedScheduleId] = useState(null);
  const [linkedSchedule, setLinkedSchedule] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [pendingDocs, setPendingDocs] = useState([]);
  const [pendingDocFile, setPendingDocFile] = useState(null);
  const [docForm, setDocForm] = useState({ title: '', type: '' });
  const [showDocModal, setShowDocModal] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const docInputRef = useRef(null);

  useEffect(() => {
    if (initialConsultationId) {
      loadConsultation(initialConsultationId);
    }
  }, [initialConsultationId]);

  const loadConsultation = async (id) => {
    const result = await medicalRecordService.getConsultation(id);
    if (result.success && result.consultation) {
      const c = result.consultation;
      setForm({
        chiefComplaint: c.chiefComplaint || '',
        historyAndObservations: c.historyAndObservations || '',
        weight: c.weight || '',
        temperature: c.temperature || '',
        heartRate: c.heartRate || '',
        respiratoryRate: c.respiratoryRate || '',
        generalCondition: c.generalCondition || '',
        hydrationLevel: c.hydrationLevel || '',
        physicalExamNotes: c.physicalExamNotes || '',
        clinicalDiagnosis: c.clinicalDiagnosis || '',
        requestedExams: c.requestedExams ? (
          typeof c.requestedExams === 'string' ? JSON.parse(c.requestedExams) : c.requestedExams
        ) : [],
        generalGuidelines: c.generalGuidelines || '',
        needsReturn: c.needsReturn || '',
        returnDeadline: c.returnDeadline || '',
        returnPriority: c.returnPriority || '',
        returnNotes: c.returnNotes || '',
        internalNotes: c.internalNotes || '',
      });
      setMedications(c.medications || []);
      setDocuments(c.Documents || []);
      setLinkedScheduleId(c.scheduleId || null);
      setLinkedSchedule(c.schedule || null);
    }
  };

  const loadDocuments = async (id) => {
    const result = await medicalRecordService.getDocuments({ consultationId: id });
    if (result.success) setDocuments(result.documents);
  };

  const inferDocType = (file) => {
    if (file.type === 'application/pdf') return 'PDF';
    if (/\.(jpg|jpeg|png|webp)$/.test(file.name.toLowerCase()) || file.type.startsWith('image/')) return 'IMG';
    return 'DOC';
  };

  const handleDocFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingDocFile(file);
    setDocForm({ title: file.name.replace(/\.[^.]+$/, ''), type: inferDocType(file) });
    setShowDocModal(true);
    e.target.value = '';
  };

  const handleDocModalConfirm = () => {
    if (!docForm.title.trim()) { toast.error('Título é obrigatório'); return; }
    if (!docForm.type) { toast.error('Tipo é obrigatório'); return; }
    setPendingDocs((prev) => [...prev, { file: pendingDocFile, title: docForm.title.trim(), type: docForm.type }]);
    setShowDocModal(false);
    setPendingDocFile(null);
  };

  const handleDeleteDoc = async (doc) => {
    if (!doc.id) {
      setPendingDocs((prev) => prev.filter((_, i) => i !== doc._pendingIndex));
      return;
    }
    const result = await medicalRecordService.deleteDocument(doc.id);
    if (result.success) {
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
      toast.success('Documento removido');
    } else {
      toast.error(result.error || 'Erro ao remover documento');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleExamToggle = (id) => {
    setForm((prev) => ({
      ...prev,
      requestedExams: prev.requestedExams.includes(id)
        ? prev.requestedExams.filter((e) => e !== id)
        : [...prev.requestedExams, id],
    }));
  };

  const handleMedChange = (field, value) => {
    setMedForm((prev) => {
      const updated = { ...prev, [field]: value };
      if (['startDate', 'durationTimes', 'durationType'].includes(field)) {
        const sd = field === 'startDate' ? value : updated.startDate;
        const dt = field === 'durationTimes' ? value : updated.durationTimes;
        const ty = field === 'durationType' ? value : updated.durationType;
        if (sd && dt && ty) {
          const n = parseInt(dt, 10);
          const unitMap = { days: 'day', weeks: 'week', months: 'month' };
          updated.endDate = isNaN(n) ? '' : dayjs(sd).add(n, unitMap[ty] || 'day').format('YYYY-MM-DD');
        } else {
          updated.endDate = '';
        }
      }
      return updated;
    });
  };

  const handleSaveMed = async () => {
    if (!medForm.name.trim()) { toast.error('Informe o nome do medicamento'); return; }
    if (!medForm.dosage.trim()) { toast.error('Informe a dosagem'); return; }
    setSavingMed(true);
    const isEdit = editingMedIndex !== null;
    const existingMed = isEdit ? medications[editingMedIndex] : null;

    if (consultationId) {
      let result;
      if (existingMed?.id) {
        result = await medicalRecordService.updateMedication(consultationId, existingMed.id, medForm);
      } else {
        result = await medicalRecordService.saveMedication(consultationId, medForm);
      }
      if (result.success) {
        if (isEdit) {
          setMedications((prev) => prev.map((m, i) => i === editingMedIndex ? result.medication : m));
        } else {
          setMedications((prev) => [...prev, result.medication]);
        }
        toast.success(isEdit ? 'Medicamento atualizado!' : 'Medicamento adicionado!');
      } else {
        toast.error(result.error || 'Erro ao salvar medicamento');
        setSavingMed(false);
        return;
      }
    } else {
      if (isEdit) {
        setMedications((prev) => prev.map((m, i) => i === editingMedIndex ? { ...medForm } : m));
      } else {
        setMedications((prev) => [...prev, { ...medForm }]);
      }
      toast.success(isEdit ? 'Medicamento atualizado!' : 'Medicamento adicionado!');
    }
    setSavingMed(false);
    setShowMedForm(false);
    setMedForm({ ...EMPTY_MED });
    setEditingMedIndex(null);
  };

  const handleDeleteMed = async (index) => {
    const med = medications[index];
    if (med.id && consultationId) {
      const result = await medicalRecordService.deleteMedication(consultationId, med.id);
      if (!result.success) { toast.error(result.error || 'Erro ao remover medicamento'); return; }
    }
    setMedications((prev) => prev.filter((_, i) => i !== index));
    toast.success('Medicamento removido!');
  };

  const handleEditMed = (index) => {
    const med = medications[index];
    setMedForm({
      name: med.name || '',
      dosage: med.dosage || '',
      frequencyTimes: med.frequencyTimes || '',
      frequencyType: med.frequencyType || '',
      durationTimes: String(med.durationTimes || ''),
      durationType: med.durationType || '',
      startDate: med.startDate || '',
      endDate: med.endDate || '',
      notes: med.notes || '',
    });
    setEditingMedIndex(index);
    setShowMedForm(true);
  };

  const buildPayload = () => ({
    petId,
    entityId,
    ...(scheduleRef?.id && !consultationId ? { scheduleId: scheduleRef.id } : {}),
    chiefComplaint: form.chiefComplaint || null,
    historyAndObservations: form.historyAndObservations || null,
    weight: form.weight || null,
    temperature: form.temperature || null,
    heartRate: form.heartRate || null,
    respiratoryRate: form.respiratoryRate || null,
    generalCondition: form.generalCondition || null,
    hydrationLevel: form.hydrationLevel || null,
    physicalExamNotes: form.physicalExamNotes || null,
    clinicalDiagnosis: form.clinicalDiagnosis || null,
    requestedExams: form.requestedExams.length ? JSON.stringify(form.requestedExams) : null,
    generalGuidelines: form.generalGuidelines || null,
    needsReturn: form.needsReturn || null,
    returnDeadline: form.returnDeadline || null,
    returnPriority: form.returnPriority || null,
    returnNotes: form.returnNotes || null,
    internalNotes: form.internalNotes || null,
  });

  const handleSave = async () => {
    if (!consultationId && !form.chiefComplaint.trim()) {
      toast.error('Queixa Principal é obrigatória');
      return;
    }
    setSaving(true);
    let result;
    if (consultationId) {
      result = await medicalRecordService.updateConsultation(consultationId, buildPayload());
    } else {
      result = await medicalRecordService.createConsultation(buildPayload());
    }
    if (!result.success) {
      toast.error(result.error || 'Erro ao salvar consulta');
      setSaving(false);
      return;
    }
    const savedId = result.consultation?.id || consultationId;
    setConsultationId(savedId);

    // Save pending medications (those without an id)
    const pendingMeds = medications.filter((m) => !m.id);
    for (const med of pendingMeds) {
      await medicalRecordService.saveMedication(savedId, med);
    }

    // Upload pending documents
    if (pendingDocs.length > 0) {
      setUploadingDoc(true);
      for (const pd of pendingDocs) {
        await medicalRecordService.uploadDocument(pd.file, {
          petId,
          entityId,
          consultationId: savedId,
          title: pd.title,
          type: pd.type,
        });
      }
      setPendingDocs([]);
      setUploadingDoc(false);
    }

    toast.success('Consulta salva com sucesso!');
    setSaving(false);
    onSaved && onSaved(result.consultation);
  };

  return (
    <div className="cf-container">
      <div className="cf-header">
        <h2 className="cf-title">{consultationId ? 'Editar Consulta' : 'Nova Consulta'}</h2>
      </div>

      {(scheduleRef && !consultationId) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          background: '#EFF6FF', border: '1px solid #BFDBFE',
          borderRadius: '10px', padding: '12px 16px', marginBottom: '16px',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <div>
            <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#1D4ED8' }}>Consulta vinculada ao agendamento</p>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#3B82F6' }}>
              {scheduleRef.pet?.name && <strong>{scheduleRef.pet.name}</strong>}
              {scheduleRef.dateTime && (
                <> &mdash; {new Date(scheduleRef.dateTime).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</>
              )}
              {scheduleRef.service?.name && <> &mdash; {scheduleRef.service.name}</>}
            </p>
          </div>
        </div>
      )}

      {linkedScheduleId && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          background: '#EFF6FF', border: '1px solid #BFDBFE',
          borderRadius: '10px', padding: '12px 16px', marginBottom: '16px',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#1D4ED8' }}>Consulta vinculada ao agendamento <span style={{ fontWeight: '400' }}>#{linkedScheduleId}</span></p>
            {linkedSchedule && (
              <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#3B82F6' }}>
                {linkedSchedule.dateTime && new Date(linkedSchedule.dateTime).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                {linkedSchedule.durationMinutes && <> &mdash; {linkedSchedule.durationMinutes} min</>}
                {linkedSchedule.status && <> &mdash; {({ scheduled: 'Agendado', confirmed: 'Confirmado', in_progress: 'Em atendimento', completed: 'Finalizado', canceled: 'Cancelado', no_show: 'Não compareceu' })[linkedSchedule.status] || linkedSchedule.status}</>}
              </p>
            )}
          </div>
        </div>
      )}

      {/* 1. Anamnese */}
      <Section title="Anamnese" defaultOpen={true} filled={!!(form.chiefComplaint || form.historyAndObservations)} icon={
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
        </svg>
      }>
        <div className="cf-field">
          <label className="form-label">Queixa Principal {!consultationId && <span style={{ color: '#DC2626' }}>*</span>}</label>
          <textarea name="chiefComplaint" className="form-textarea" value={form.chiefComplaint} onChange={handleChange} placeholder="Motivo da consulta..." />
        </div>
        <div className="cf-field">
          <label className="form-label">Histórico e Observações</label>
          <textarea name="historyAndObservations" className="form-textarea" value={form.historyAndObservations} onChange={handleChange} placeholder="Histórico do animal, observações..." style={{ minHeight: '100px' }} />
        </div>
      </Section>

      {/* 2. Exame Físico */}
      <Section title="Exame Físico" filled={!!(form.weight || form.temperature || form.heartRate || form.respiratoryRate || form.generalCondition || form.hydrationLevel || form.physicalExamNotes)} icon={
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
      }>
        <div className="cf-grid-4">
          <div className="cf-field">
            <label className="form-label">Peso (kg)</label>
            <input type="number" name="weight" className="form-input" value={form.weight} onChange={handleChange} placeholder="0.0" step="0.1" />
          </div>
          <div className="cf-field">
            <label className="form-label">Temperatura (°C)</label>
            <input type="number" name="temperature" className="form-input" value={form.temperature} onChange={handleChange} placeholder="38.5" step="0.1" />
          </div>
          <div className="cf-field">
            <label className="form-label">Freq. Cardíaca (bpm)</label>
            <input type="number" name="heartRate" className="form-input" value={form.heartRate} onChange={handleChange} placeholder="80" />
          </div>
          <div className="cf-field">
            <label className="form-label">Freq. Respiratória</label>
            <input type="number" name="respiratoryRate" className="form-input" value={form.respiratoryRate} onChange={handleChange} placeholder="20" />
          </div>
        </div>
        <div className="cf-grid-2">
          <div className="cf-field">
            <label className="form-label">Estado Geral</label>
            <select name="generalCondition" className="form-select" value={form.generalCondition} onChange={handleChange}>
              <option value="">Selecionar...</option>
              {GENERAL_CONDITION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="cf-field">
            <label className="form-label">Hidratação</label>
            <select name="hydrationLevel" className="form-select" value={form.hydrationLevel} onChange={handleChange}>
              <option value="">Selecionar...</option>
              {HYDRATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <div className="cf-field">
          <label className="form-label">Observações</label>
          <textarea name="physicalExamNotes" className="form-textarea" value={form.physicalExamNotes} onChange={handleChange} placeholder="Adicione observações relevantes do exame físico" style={{ minHeight: '100px' }} />
        </div>
      </Section>

      {/* 3. Diagnóstico e Conduta */}
      <Section title="Diagnóstico e Conduta" filled={!!(form.clinicalDiagnosis || form.requestedExams?.length || form.generalGuidelines)} icon={
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      }>
        <div className="cf-field">
          <label className="form-label">Diagnóstico Clínico</label>
          <textarea name="clinicalDiagnosis" className="form-textarea" value={form.clinicalDiagnosis} onChange={handleChange} placeholder="Diagnóstico..." />
        </div>
        <div className="cf-field">
          <label className="form-label">Exames Solicitados</label>
          <div className="cf-exams-grid">
            {EXAM_OPTIONS.map((exam) => (
              <button
                key={exam.id}
                type="button"
                className={`cf-exam-chip ${form.requestedExams.includes(exam.id) ? 'selected' : ''}`}
                onClick={() => handleExamToggle(exam.id)}
              >
                {form.requestedExams.includes(exam.id) && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                )}
                {exam.label}
              </button>
            ))}
          </div>
        </div>
        <div className="cf-field">
          <label className="form-label">Orientações Gerais</label>
          <textarea name="generalGuidelines" className="form-textarea" value={form.generalGuidelines} onChange={handleChange} placeholder="Orientações ao tutor..." />
        </div>
      </Section>

      {/* 4. Prescrição */}
      <Section title="Prescrição (Medicamentos)" filled={medications.length > 0} count={medications.length > 0 ? `${medications.length} ${medications.length === 1 ? 'medicamento' : 'medicamentos'}` : null} icon={
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
        </svg>
      }>
        <div className="cf-med-list">
          {medications.map((med, index) => {
            const FREQ_TIMES = { once: '1 vez', twice: '2 vezes', three_times: '3 vezes', four_times: '4 vezes' };
            const FREQ_TYPE = { by_day: 'ao dia', by_month: 'ao mês', by_year: 'ao ano' };
            const DUR_TYPE = { days: 'dias', weeks: 'semanas', months: 'meses' };
            const freq = [FREQ_TIMES[med.frequencyTimes], FREQ_TYPE[med.frequencyType]].filter(Boolean).join(' ');
            const dur = med.durationTimes ? `${med.durationTimes} ${DUR_TYPE[med.durationType] || med.durationType || ''}`.trim() : null;
            const period = med.startDate && med.endDate
              ? `${dayjs(med.startDate).format('DD/MM')} – ${dayjs(med.endDate).format('DD/MM/YYYY')}`
              : med.startDate ? `a partir de ${dayjs(med.startDate).format('DD/MM/YYYY')}` : null;
            return (
            <div key={index} className="cf-med-item">
              <div className="cf-med-info">
                <h4>{med.name} <span style={{ fontWeight: '400', color: '#6b7280' }}>— {med.dosage}</span></h4>
                {(freq || dur) && (
                  <p>{[freq, dur ? `por ${dur}` : null].filter(Boolean).join(' · ')}</p>
                )}
                {period && (
                  <p style={{ fontSize: '11px', color: '#9ca3af', margin: '1px 0 0' }}>{period}</p>
                )}
                {med.notes && <p style={{ fontStyle: 'italic' }}>{med.notes}</p>}
              </div>
              <div className="cf-med-actions">
                <button className="cf-med-edit-btn" onClick={() => handleEditMed(index)} title="Editar">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                </button>
                <button className="cf-med-delete-btn" onClick={() => handleDeleteMed(index)} title="Remover">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                </button>
              </div>
            </div>
            );
          })}
        </div>

        {showMedForm ? (
          <div className="cf-med-form">
            <div className="cf-grid-2" style={{ marginBottom: '10px' }}>
              <div className="cf-field">
                <label className="form-label">Nome *</label>
                <input type="text" className="form-input" value={medForm.name} onChange={(e) => handleMedChange('name', e.target.value)} placeholder="Ex: Amoxicilina" />
              </div>
              <div className="cf-field">
                <label className="form-label">Dosagem *</label>
                <input type="text" className="form-input" value={medForm.dosage} onChange={(e) => handleMedChange('dosage', e.target.value)} placeholder="Ex: 500mg" />
              </div>
            </div>
            <div className="cf-grid-3" style={{ marginBottom: '10px' }}>
              <div className="cf-field">
                <label className="form-label">Frequência</label>
                <select className="form-select" value={medForm.frequencyTimes} onChange={(e) => handleMedChange('frequencyTimes', e.target.value)}>
                  <option value="">—</option>
                  {FREQUENCY_TIMES_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="cf-field">
                <label className="form-label">Por</label>
                <select className="form-select" value={medForm.frequencyType} onChange={(e) => handleMedChange('frequencyType', e.target.value)}>
                  <option value="">—</option>
                  {FREQUENCY_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="cf-field">
                <label className="form-label">Início</label>
                <input type="date" className="form-input" value={medForm.startDate} onChange={(e) => handleMedChange('startDate', e.target.value)} />
              </div>
            </div>
            <div className="cf-grid-3" style={{ marginBottom: '10px' }}>
              <div className="cf-field">
                <label className="form-label">Duração</label>
                <input type="number" className="form-input" value={medForm.durationTimes} onChange={(e) => handleMedChange('durationTimes', e.target.value)} placeholder="7" min="1" />
              </div>
              <div className="cf-field">
                <label className="form-label">Unidade</label>
                <select className="form-select" value={medForm.durationType} onChange={(e) => handleMedChange('durationType', e.target.value)}>
                  <option value="">—</option>
                  {DURATION_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="cf-field">
                <label className="form-label">Fim</label>
                <input type="date" className="form-input" value={medForm.endDate} readOnly style={{ background: '#f9f9f9' }} />
              </div>
            </div>
            <div className="cf-field" style={{ marginBottom: '12px' }}>
              <label className="form-label">Observações</label>
              <input type="text" className="form-input" value={medForm.notes} onChange={(e) => handleMedChange('notes', e.target.value)} placeholder="Instruções especiais..." />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button type="button" className="btn-secondary" style={{ padding: '8px 16px', fontSize: '13px' }} onClick={() => { setShowMedForm(false); setMedForm({ ...EMPTY_MED }); setEditingMedIndex(null); }}>
                Cancelar
              </button>
              <button type="button" className="btn-primary" style={{ padding: '8px 16px', fontSize: '13px' }} onClick={handleSaveMed} disabled={savingMed}>
                {savingMed ? 'Salvando...' : editingMedIndex !== null ? 'Atualizar' : 'Adicionar'}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => { setShowMedForm(true); setMedForm({ ...EMPTY_MED }); setEditingMedIndex(null); }}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: '1.5px dashed #ccc', borderRadius: '10px', padding: '10px 16px', cursor: 'pointer', color: '#666', fontSize: '13px', fontWeight: '500', width: '100%', justifyContent: 'center' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Adicionar medicamento
          </button>
        )}
      </Section>

      {/* 5. Retorno */}
      <Section title="Retorno" filled={!!(form.needsReturn || form.returnDeadline || form.returnNotes)} icon={
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" />
        </svg>
      }>
        <div className="cf-grid-3">
          <div className="cf-field">
            <label className="form-label">Precisa de Retorno</label>
            <select name="needsReturn" className="form-select" value={form.needsReturn} onChange={handleChange}>
              <option value="">Selecionar...</option>
              {NEEDS_RETURN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {form.needsReturn === 'yes' || form.needsReturn === 'if_needed' ? (
            <>
              <div className="cf-field">
                <label className="form-label">Prazo (dias)</label>
                <input type="number" name="returnDeadline" className="form-input" value={form.returnDeadline} onChange={handleChange} placeholder="30" min="1" />
              </div>
              <div className="cf-field">
                <label className="form-label">Prioridade</label>
                <select name="returnPriority" className="form-select" value={form.returnPriority} onChange={handleChange}>
                  <option value="">Selecionar...</option>
                  {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </>
          ) : null}
        </div>
        {(form.needsReturn === 'yes' || form.needsReturn === 'if_needed') && (
          <div className="cf-field">
            <label className="form-label">Observações de Retorno</label>
            <input type="text" name="returnNotes" className="form-input" value={form.returnNotes} onChange={handleChange} placeholder="Observações..." />
          </div>
        )}
      </Section>

      {/* 6. Notas Internas */}
      <Section title="Notas Internas" filled={!!form.internalNotes} icon={
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      }>
        <div className="cf-field">
          <label className="form-label">Notas (não visível ao tutor)</label>
          <textarea name="internalNotes" className="form-textarea" value={form.internalNotes} onChange={handleChange} placeholder="Anotações internas..." style={{ minHeight: '80px' }} />
        </div>
      </Section>

      {/* 7. Documentos */}
      <Section title="Documentos" filled={documents.length > 0 || pendingDocs.length > 0} count={documents.length + pendingDocs.length > 0 ? `${documents.length + pendingDocs.length} ${documents.length + pendingDocs.length === 1 ? 'arquivo' : 'arquivos'}` : null} icon={
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
        </svg>
      }>
        {/* Modal de detalhes do documento */}
        {showDocModal && (
          <div className="mr-modal-overlay" onClick={() => setShowDocModal(false)}>
            <div className="mr-modal" style={{ maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
              <div className="mr-modal-header">
                <h2 className="mr-modal-title">Detalhes do documento</h2>
                <button className="mr-modal-close" onClick={() => setShowDocModal(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </div>
              <div className="mr-modal-body">
                <p style={{ fontSize: '13px', color: '#666', margin: '0 0 16px', wordBreak: 'break-all' }}>
                  Arquivo: <strong>{pendingDocFile?.name}</strong>
                </p>
                <div className="cf-field" style={{ marginBottom: '16px' }}>
                  <label className="form-label">Título *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={docForm.title}
                    onChange={(e) => setDocForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="Ex: Exame de sangue, Raio-X..."
                  />
                </div>
                <div className="cf-field" style={{ marginBottom: '24px' }}>
                  <label className="form-label">Tipo *</label>
                  <select
                    className="form-select"
                    value={docForm.type}
                    onChange={(e) => setDocForm((f) => ({ ...f, type: e.target.value }))}
                  >
                    <option value="">Selecionar...</option>
                    <option value="PDF">PDF</option>
                    <option value="IMG">Imagem</option>
                    <option value="DOC">Documento</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button className="btn-secondary" style={{ padding: '10px 20px' }} onClick={() => setShowDocModal(false)}>Cancelar</button>
                  <button className="btn-primary" style={{ padding: '10px 20px' }} onClick={handleDocModalConfirm}>Adicionar</button>
                </div>
              </div>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => docInputRef.current?.click()}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: '1.5px dashed #ccc', borderRadius: '10px', padding: '10px 16px', cursor: 'pointer', color: '#666', fontSize: '13px', fontWeight: '500', width: '100%', justifyContent: 'center', marginBottom: '12px' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          Anexar documento
        </button>
        <input
          ref={docInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          style={{ display: 'none' }}
          onChange={handleDocFileSelected}
        />

        {[...documents, ...pendingDocs.map((pd, i) => ({ ...pd, _pendingIndex: i, _pending: true }))].length > 0 && (
          <div className="mr-documents-list">
            {documents.map((doc) => (
              <div key={doc.id} className="mr-doc-row">
                <div className={`mr-doc-icon ${doc.type === 'IMG' ? 'img' : 'pdf'}`}>
                  {doc.type === 'IMG'
                    ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                    : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                  }
                </div>
                <div className="mr-doc-info">
                  <span className="mr-doc-title">{doc.title || doc.fileName}</span>
                  <div className="mr-doc-meta">
                    <span className={`mr-doc-badge ${doc.type === 'IMG' ? 'img' : 'pdf'}`}>{doc.type}</span>
                    <span className="mr-doc-date">{dayjs(doc.createdAt).format('DD/MM/YYYY')}</span>
                  </div>
                </div>
                <div className="mr-doc-actions">
                  {doc.fileUrl && (
                    <a href={doc.fileUrl} target="_blank" rel="noreferrer" className="mr-doc-download" title="Baixar">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                      <span>Baixar</span>
                    </a>
                  )}
                  <button type="button" onClick={() => handleDeleteDoc(doc)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px', display: 'flex', alignItems: 'center' }} title="Remover">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" /></svg>
                  </button>
                </div>
              </div>
            ))}
            {pendingDocs.map((pd, i) => (
              <div key={`pending-${i}`} className="mr-doc-row">
                <div className={`mr-doc-icon ${pd.type === 'IMG' ? 'img' : 'pdf'}`}>
                  {pd.type === 'IMG'
                    ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                    : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                  }
                </div>
                <div className="mr-doc-info">
                  <span className="mr-doc-title">{pd.title}</span>
                  <div className="mr-doc-meta">
                    <span className={`mr-doc-badge ${pd.type === 'IMG' ? 'img' : 'pdf'}`}>{pd.type}</span>
                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#D97706', background: '#FEF3C7', padding: '2px 7px', borderRadius: '20px' }}>Pendente</span>
                  </div>
                </div>
                <div className="mr-doc-actions">
                  <button type="button" onClick={() => setPendingDocs((prev) => prev.filter((_, idx) => idx !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px', display: 'flex', alignItems: 'center' }} title="Remover">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" /></svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <div className="cf-footer">
        {onCancel && (
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancelar
          </button>
        )}
        <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Salvando...' : consultationId ? 'Atualizar Consulta' : 'Salvar Consulta'}
        </button>
      </div>
    </div>
  );
}

export default ConsultationForm;
